import { IBFRecord } from "@/lib/drivers/insulet/IBFRecords/IBFRecord";

export type IBFRecordParsingInfo = {
  isOmnipodDash: boolean;
  lastFetchedTimestamp: number;
};

export type IBFRecordParsingDefinition<T> = {
  value: number;
  name: string;
  size: number;
  parse: (record: IBFRecord, info: IBFRecordParsingInfo) => T;
};

export type IBFBatchRecordParsingDefinition<T, U> = {
  value: number;
  name: string;
  recordsCount: number | "all";
  definition: IBFRecordParsingDefinition<T>;
  parse: (parsedRecord: IBFParsedRecord<T>[], info: IBFRecordParsingInfo) => U;
};

export type IBFParsedRecord<T> = {
  value: number;
  name: string;
  data: T;
  raw: ArrayBuffer | ArrayBuffer[];
};

type RecordParserErrorType = "RECORD_SIZE_MISMATCH" | "RECORD_VALUE_MISMATCH" | "EXPECTED_RECORD_MISSING";

export class RecordParserError extends Error {
  constructor(public type: RecordParserErrorType) {
    super(type);
  }
}

export const recordParser = <T>(
  records: IBFRecord[],
  definition: IBFRecordParsingDefinition<T>,
  info: IBFRecordParsingInfo,
): IBFParsedRecord<T> => {
  const record = records.shift();
  if (!record) {
    throw new RecordParserError("EXPECTED_RECORD_MISSING");
  }
  // Ensure offset is at 0
  record.restart();
  // Size of the payload + 2 for the checksum
  if (record.remaining() < definition.size + 2) {
    throw new RecordParserError("RECORD_SIZE_MISMATCH");
  }

  try {
    return {
      value: definition.value,
      name: definition.name,
      data: definition.parse(record, info),
      raw: record.buffer.buffer,
    };
  } catch (e) {
    console.error("Error parsing record", e);
    throw new RecordParserError("RECORD_VALUE_MISMATCH");
  }
};

export const batchRecordParser = <T, U>(
  records: IBFRecord[],
  batchDefinition: IBFBatchRecordParsingDefinition<T, U>,
  info: IBFRecordParsingInfo,
  loopCondition?: (parsedRecord: IBFParsedRecord<T>, index: number) => boolean,
): IBFParsedRecord<U> => {
  let recordsCount = batchDefinition.recordsCount;
  if (recordsCount === "all") {
    recordsCount = records.length;
  }
  if (records.length < recordsCount) {
    throw new RecordParserError("EXPECTED_RECORD_MISSING");
  }
  const parsedRecords: IBFParsedRecord<T>[] = [];
  for (let i = 0; i < recordsCount; i++) {
    const record = recordParser(records, batchDefinition.definition, info);
    parsedRecords.push(record);
    if (loopCondition && !loopCondition(record, i)) {
      break;
    }
  }

  return {
    value: batchDefinition.value,
    name: batchDefinition.name,
    data: batchDefinition.parse(parsedRecords, info),
    raw: parsedRecords.map(parsedRecord => parsedRecord.raw).flat(),
  };
};
